import { useEffect, useState } from "react";
import { collection, query, getDocs, doc, updateDoc, orderBy, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { CheckCircle, XCircle, Clock, User, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_COLOR = {
  pending: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
  approved: "text-[#00ff77] bg-[#00ff77]/10 border-[#00ff77]/20",
  restricted: "text-red-400 bg-red-400/10 border-red-400/20",
};

export default function AdminCreators() {
  const [creators, setCreators] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadCreators(); }, []);

  const loadCreators = async () => {
    try {
      const snap = await getDocs(query(collection(db, "creators"), orderBy("createdAt", "desc")));
      setCreators(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, "creators", id), { status });
    // Update user role on approval
    if (status === "approved") {
      await updateDoc(doc(db, "users", id), { role: "creator" });
    }
    // Write notification to creator
    const message =
      status === "approved"
        ? "Your creator application has been approved! Welcome to StagePass."
        : "Your creator account has been restricted. Contact stagepassadmin@gmail.com for support.";
    try {
      await addDoc(collection(db, "notifications", id, "items"), {
        type: status === "approved" ? "creator_approved" : "creator_restricted",
        message,
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (e) { console.error("Notification write error:", e); }
    setCreators((c) => c.map((x) => x.id === id ? { ...x, status } : x));
  };

  const filtered = creators.filter((c) => filter === "all" || c.status === filter);

  return (
    <div data-testid="admin-creators" className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1"><ChevronLeft size={16} /> Admin</Link>
        <h2 className="text-xl font-black text-white">Creators</h2>
      </div>

      <div className="flex gap-2">
        {["pending", "approved", "restricted", "all"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${filter === s ? "bg-[#00ff77]/20 text-[#00ff77] border border-[#00ff77]/30" : "bg-[#111] border border-white/10 text-zinc-400 hover:border-white/20"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-[#111] rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <p className="text-zinc-500 text-sm py-8 text-center">No creators with status: {filter}</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((creator) => (
            <div key={creator.id} data-testid={`creator-row-${creator.id}`}
              className="flex items-center gap-4 bg-[#111] border border-white/5 rounded-xl p-4">
              <div className="w-10 h-10 rounded-full bg-[#1a1a1a] overflow-hidden flex-shrink-0">
                {creator.photoURL ? (
                  <img src={creator.photoURL} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><User size={16} className="text-zinc-600" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{creator.displayName || "—"}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize ${STATUS_COLOR[creator.status] || STATUS_COLOR.pending}`}>
                    {creator.status || "pending"}
                  </span>
                  {creator.genres && creator.genres.slice(0, 2).map((g) => (
                    <span key={g} className="text-zinc-600 text-[10px] bg-white/5 px-1.5 py-0.5 rounded-full">{g}</span>
                  ))}
                  {creator.location && <span className="text-zinc-600 text-xs">{creator.location}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {creator.slug && (
                  <Link to={`/c/${creator.slug}`} className="text-zinc-500 hover:text-white text-xs border border-white/10 px-2 py-1 rounded-lg hover:border-white/30 transition-colors">
                    View
                  </Link>
                )}
                {creator.status !== "approved" && (
                  <button onClick={() => updateStatus(creator.id, "approved")} data-testid={`approve-creator-${creator.id}`}
                    className="flex items-center gap-1 text-xs bg-[#00ff77]/10 border border-[#00ff77]/20 text-[#00ff77] px-3 py-1.5 rounded-lg hover:bg-[#00ff77]/20 transition-colors">
                    <CheckCircle size={12} /> Approve
                  </button>
                )}
                {creator.status !== "restricted" && (
                  <button onClick={() => updateStatus(creator.id, "restricted")} data-testid={`restrict-creator-${creator.id}`}
                    className="flex items-center gap-1 text-xs bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-1.5 rounded-lg hover:bg-red-500/20 transition-colors">
                    <XCircle size={12} /> Restrict
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
